// 1. Purpose: The CompareWidgetPLP component allows users to compare multiple products by displaying their details side by side on the Product Listing Page (PLP).
// 2. Features Displayed: It includes product images, names, ratings, prices, and features in a structured layout for easy comparison.
// 3. Interactivity: Users can navigate to individual product pages, remove products from the comparison list, or return to shopping using provided buttons.
// 4. Session Storage: Manages selected products using session storage, ensuring the comparison data persists during the session.
// 5. Responsive Design: Designed to handle up to 4 products, dynamically displaying placeholders for missing products.

import React, { useEffect, useState } from 'react';
import Prices from '../../../product-tile/prices';
import { useRouter } from 'next/navigation';
import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import Link from 'components/commercetools-ui/atoms/link';
import useLocale from 'helpers/hooks/useLocale';
import beforeCompare from 'public/locales/en/product.json';
import { Product } from '../Algolia-search-Page/Hit';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';
import Price from '../Algolia-search-Page/price';
const FeaturesList = ({ featuresHtml }: { featuresHtml: string }) => {
  const div = document.createElement('div');
  div.innerHTML = featuresHtml;

  const featureItems = Array.from(div.querySelectorAll('li')).map((li) => li.textContent);

  return (
    <ul className="ml-5 list-disc space-y-2 pl-5">
      {featureItems.map((feature, index) => (
        <li key={index} className="text-start">
          {feature}
        </li>
      ))}
    </ul>
  );
};
interface ProductName {
  [locale: string]: string;
}

const CompareWidgetPLP = () => {
  const storedProducts = sessionStorage.getItem('PLP-Compare');
  const parsedData = storedProducts ? JSON?.parse(storedProducts) : [];
  const router = useRouter();
  const { locale } = useLocale();
  const [productsToCompare, setProductsToCompare] = useState<any>(parsedData);

  const selectItem = () => {
    const url = productsToCompare.products?.length === 0 ? `/` : `${parsedData?.query}`;

    router.push(url);
  };

  const convertProductNameToUrl = (input: string) => {
    const urlformat = `${input?.toLowerCase().replace(/\s+/g, '-')}`;
    const url = urlformat?.endsWith('-') ? urlformat : `${urlformat}-`;
    return url?.replace(/,/g, '');
  };

  const removeProductFromSession = (productKey: string) => {
    setProductsToCompare((prevProducts: any) => {
      const products = prevProducts;

      const updatedProducts = products.products.filter((product: any) => product.productKey !== productKey);

      const updatedCompareProducts = { query: prevProducts.query, products: updatedProducts };
      sessionStorage.setItem('PLP-Compare', JSON.stringify(updatedCompareProducts));

      return updatedCompareProducts;
    });
  };

  return (
    <>
      <div className="flex justify-center">
        <p className="mb-[25px] text-34 font-semibold">Compare items</p>
      </div>
      <Link link={`${productsToCompare?.products?.length === 0}` ? '/' : `${`/search?q=${parsedData?.[0]?.query}`}`}>
        <div className="ml-20 flex justify-start  px-15">
          <ChevronLeftIcon
            className="mt-2 h-15 w-18 font-bold text-[#127e57]"
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
          />
          <p>Continue Shopping</p>
        </div>
      </Link>
      <div className="mt-20 overflow-x-hidden">
        {' '}
        <div>
          <div className="ml-[6rem] flex w-[80%] justify-evenly overflow-x-hidden px-30 py-50">
            {' '}
            {Array.from({ length: 4 }).map((_, index) => {
              const product = productsToCompare?.products?.[index];
              const item = beforeCompare.beforeComparePage[0];

              return (
                <React.Fragment key={index}>
                  {product ? (
                    <div className="flex w-[200px] flex-col items-center justify-center">
                      <div className="flex w-[240px] flex-col items-center border-slate-700">
                        <div className="h-[238px] w-[233px]">
                          <img src={product.variants?.[0]?.images?.[0]} className="h-full w-full" />
                        </div>
                        <hr className="my-2 w-full border-t border-slate-500" />
                        <p className="mb-20 mt-10 h-59 w-full text-center text-22 font-bold leading-[22px]">
                          {product?.name as any}
                        </p>
                        <div className="flex items-center justify-center text-center">
                          <Link
                            link={`/${
                              convertProductNameToUrl(product?.name as any) + (product?.slug as any) + '.html'
                            }`}
                          >
                            <input
                              type="button"
                              className="h-40 min-h-[40px] w-[102px] cursor-pointer rounded-md border bg-[#1DCE8F] px-4 py-2 text-14 font-semibold leading-none"
                              value={'SHOP NOW'}
                            />
                          </Link>
                        </div>
                        <div
                          className="flex items-center"
                          onClick={() => removeProductFromSession(product?.productKey)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            x="0px"
                            y="0px"
                            width="15"
                            height="15"
                            viewBox="0 0 48 48"
                          >
                            <path
                              fill="#F44336"
                              d="M21.5 4.5H26.501V43.5H21.5z"
                              transform="rotate(45.001 24 24)"
                            ></path>
                            <path
                              fill="#F44336"
                              d="M21.5 4.5H26.5V43.501H21.5z"
                              transform="rotate(135.008 24 24)"
                            ></path>
                          </svg>
                          <input className="cursor-pointer" type="button" value={'Remove'} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={selectItem}
                      className="flex h-[40px] w-[226px] cursor-pointer flex-col items-start justify-start border-2 border-dashed border-slate-700"
                    >
                      <p className="text-center text-16 font-normal">{item?.text}</p>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {productsToCompare?.products?.length > 0 && (
            <div className="mb-20 flex flex-row items-center justify-center">
              <div className="w-[1200px]">
                <hr className="mb-4 w-full border-t border-slate-500" />

                <div className="my-10 grid grid-cols-[120px_repeat(4,_1fr)]  items-center">
                  <p className="ml-10 text-start text-16 font-semibold">Rating</p>
                  {productsToCompare?.products.map((product: Product, index: number) => (
                    <div key={`rating-${index}`} className="text-left text-14 font-normal">
                      <ul className="ml-20  list-disc  pl-5">
                        <li className=" w-[100px]  text-start">
                          <div className="mb-10 py-7">
                            <TrustBox id={product?.variants?.[0].sku} showVertical={false} />
                          </div>
                        </li>
                      </ul>
                    </div>
                  ))}
                </div>
                <hr className="mb-4 w-full border-t border-slate-500" />

                <div className="my-10 grid grid-cols-[120px_repeat(4,_1fr)] items-center">
                  <p className="ml-10 text-start text-16 font-semibold">Price</p>
                  {productsToCompare?.products?.map((product: Product, index: number) => (
                    <div key={`rating-${index}`} className="text-left text-14 font-normal">
                      <ul className="ml-5  list-disc  pl-5">
                        <li className=" w-[100px]  text-start">
                          <Price
                            prices={product?.standalonePrice}
                            discountedPrice={product?.variants?.[0]?.discountedPrice}
                          />
                        </li>
                      </ul>
                    </div>
                  ))}
                </div>
                <hr className="mb-4 w-full border-t border-slate-500" />

                <div className="my-10 grid grid-cols-[120px_repeat(4,_1fr)] items-center">
                  <p className="ml-10 text-start text-16 font-semibold">Features</p>
                  {productsToCompare?.products?.map((product: Product, index: number) => (
                    <div key={`features-${index}`} className="text-left text-14 font-normal">
                      <FeaturesList featuresHtml={product?.description || ''} />
                    </div>
                  ))}
                </div>

                <hr className="mt-4 w-full border-t border-slate-500" />

                <div className="my-10 grid grid-cols-[120px_repeat(4,_1fr)] items-center  ">
                  <p className=""></p>
                  {productsToCompare?.products.map((product: Product, index: number) => (
                    <div key={`features-${index}`} className="text-left text-14 font-normal">
                      <div className="ml-5 flex items-center text-center">
                        <Link
                          link={`/${convertProductNameToUrl(product?.name as any) + (product?.slug as any) + '.html'}`}
                        >
                          <input
                            type="button"
                            className="h-40 min-h-[40px] w-[102px] cursor-pointer rounded-md border bg-[#1DCE8F] px-4 py-2 text-14 font-semibold leading-none"
                            value={'SHOP NOW'}
                          />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CompareWidgetPLP;
